import { useMutation } from '@apollo/client'
import { TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { GetCandidatesForRosterSql } from 'recruiter/contexts'
import DrawerComponent from 'shared/components/DrawerComponent'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem } from 'shared/utils'
import { PrependRosterCandidateInternalNoteBulk } from './BulkActions.gql'

interface AddInternalNotesProps {
    rosterId: string
    candidateIds: string[]
    isOpen: boolean
    onClose: () => void
}

const AddInternalNotes = ({
    rosterId,
    candidateIds,
    isOpen,
    onClose,
}: AddInternalNotesProps) => {
    const toast = useToast()
    const [noteContent, setNoteContent] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [prependInternalNoteMutation] = useMutation(
        PrependRosterCandidateInternalNoteBulk
    )

    const handleSubmit = async () => {
        if (!noteContent.trim()) {
            toast({
                title: 'Error',
                severity: 'error',
                message: 'Note cannot be empty',
            })
            return
        }

        setIsSubmitting(true)
        try {
            await prependInternalNoteMutation({
                variables: {
                    rosterId,
                    candidateIds,
                    note: noteContent.trim(),
                },
                refetchQueries: [GetCandidatesForRosterSql],
                onCompleted: (data) => {
                    handleClose()
                    toast({
                        title: 'Success!',
                        severity: 'success',
                        message:
                            data.prependRosterCandidateInternalNoteBulk
                                ?.message ??
                            'Internal notes added successfully',
                    })
                },
                onError: (error) => {
                    console.error(error)
                    toast({
                        title: 'Error!',
                        severity: 'error',
                        message: 'Something went wrong.',
                    })
                },
            })
        } catch (e) {
            toast({
                title: 'Error',
                message: 'Something went wrong.',
                severity: 'error',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        onClose()
        setNoteContent('')
        setIsSubmitting(false)
    }

    return (
        <DrawerComponent
            open={isOpen}
            hasCancelButton
            onPrimaryAction={handleSubmit}
            primaryActionText={`Add Notes (${candidateIds.length})`}
            onCancel={handleClose}
            anchor="right"
            onClose={handleClose}
            headerTitle="Add Internal Notes"
            saveButtonDisabled={isSubmitting || !noteContent.trim()}
        >
            <Typography variant="h5" sx={{ marginBottom: getPxAsRem(16) }}>
                Note Content
            </Typography>
            <TextField
                variant="outlined"
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Add internal note here..."
                multiline
                fullWidth
                rows={6}
                disabled={isSubmitting}
                label="Internal Note"
                sx={{
                    minHeight: getPxAsRem(150),
                    marginBottom: getPxAsRem(16),
                }}
            />
            <Typography variant="body2" color="text.secondary">
                This note will be prepended to the internal notes of{' '}
                {candidateIds.length} selected candidate(s).
            </Typography>
        </DrawerComponent>
    )
}

export default AddInternalNotes
